'use client';

import React from 'react';
import { cn } from '../../utils/cn';
import { ItemListItem } from '../../components/ItemListItem';
import { ItemPrice } from '../../components/ItemPrice';
import { OverlayBadge, Spinner } from '../../components/ui';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import translations from './UpgradeScreen.translations';
import { FaPlus, FaExclamationTriangle } from '../../components/icons';

interface UpgradeItem {
  id: string;
  hashName: string;
  name: string;
  imageUrl: string;
  rarityColor: string;
  price: number;
  statTrak: boolean;
  souvenir: boolean;
}

interface UpgradeSlotProps {
  item: UpgradeItem | null;
  type: 'input' | 'output';
  disabled?: boolean;
  onClick: () => void;
  loading?: boolean;
  error?: boolean;
  pulse?: boolean;
  height?: number;
}

export const UpgradeSlot: React.FC<UpgradeSlotProps> = ({ item, type, disabled = false, onClick, loading = false, error = false, pulse = false, height = 150 }) => {
  const { t } = useLocalTranslation(translations);

  if (item) {
    return (
      <div
        className={cn('rounded-[21px]', pulse && 'upgrade-target-pulse')}
        style={{ '--rarity-color': item.rarityColor, '--slot-height': `${height}px` }}
      >
        <ItemListItem
          item={{
            name: item.name,
            imageUrl: item.imageUrl,
            rarityColor: item.rarityColor,
          }}
          onClick={disabled ? undefined : onClick}
          className={cn(
            '!h-[var(--slot-height)]',
            type === 'output' && 'ring-2 ring-offset-2 ring-offset-black',
            disabled && !pulse && 'opacity-50 !cursor-not-allowed',
            pulse && '!cursor-default',
          )}
        >
          <OverlayBadge>
            <ItemPrice credits={item.price} variant="cardPrice" />
          </OverlayBadge>
        </ItemListItem>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        data-testid={`upgrade-slot-${type}`}
        className="flex w-full flex-col items-center justify-center gap-3 rounded-[21px] border border-white/[0.06] bg-bg-secondary"
        style={{ height }}
      >
        <Spinner size="s" />
      </div>
    );
  }

  const roleLabel = type === 'input' ? t('slotRoleSource') : t('slotRoleTarget');

  return (
    <button
      type="button"
      data-testid={`upgrade-slot-${type}`}
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      className={cn(
        'w-full rounded-[21px] flex flex-col items-center justify-center gap-3 bg-bg-secondary outline-none transition-[filter,border-color] duration-200 px-3',
        error ? 'border border-error/40' : 'border border-white/[0.06]',
        disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:brightness-110 hover:border-white/10',
      )}
      style={{ height }}
    >
      {error ? (
        <>
          <span className="grid h-12 w-12 place-items-center rounded-full bg-error/15 text-error">
            <FaExclamationTriangle className="text-xl" />
          </span>
          <span className="text-[13px] text-white/70">{t('slotRetry')}</span>
        </>
      ) : (
        <>
          <span
            className={cn(
              'grid place-items-center w-12 h-12 rounded-full',
              disabled
                ? 'bg-white/10 text-white/40'
                : 'text-black bg-[linear-gradient(135deg,#FF7A00_0%,#FFC152_100%)] shadow-[0_2px_12px_rgba(255,140,0,0.35)]',
            )}
          >
            <FaPlus className="text-xl" />
          </span>
          <span className="text-[13px] text-white/40">{roleLabel}</span>
        </>
      )}
    </button>
  );
};
